import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ErrorResponse, ListModel, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import {
    CreatePaymentBatchRequest, CreatePaymentBatchResponse, ImportTurnoverRequest, ImportTurnoverResponse, PaymentResponse, PaymentRequest,
    PaymentDropdownLists, PaymentModelForList, PaymentSliceModel, GetPaymentResponse, PaymentDraftRequest,
    PaymentDraftModelForList, UpdatePaymentDraftRequest, DraftDataResponseWithDropdowns
} from "./payment-interfaces";
import { GenerateGUID } from "shared/utils/common";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { API_BASE_URL as baseUri } from "config/env";
import { TurnoverMatchRequest, TurnoverMatchResponse, TurnoverMatchSuggestion } from "../ai-matching/turnover-matching";

// Every failure is reported where it happens, as in the other slices; the state holds data only.
const ERROR_FALLBACK = 'Error in application';

export const GetCreateData = createAsyncThunk<PaymentDropdownLists, void, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/GetCreateData',
    async (_, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/payments/get-import-turnover-dropdowns', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PaymentDropdownLists;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllPayments = createAsyncThunk<PaymentModelForList[], void, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/GetPayments',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/payments/get', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PaymentModelForList[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPaymentById = createAsyncThunk<GetPaymentResponse, string, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/GetPaymentById',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/payments/get-by-id?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPaymentResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdatePayment = createAsyncThunk<PaymentResponse, PaymentRequest, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/UpdatePayment',
    async (payment: PaymentRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/payments/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(payment)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as PaymentResponse;
            // The response carries no list row (the list shows values the backend composes), so the
            // list is read again - in the background, the table stays on its page.
            dispatch(GetAllPayments());
            dispatch(showNotification({ message: 'The payment is successfully updated.', type: NotificationTypes.Success }));
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeletePayment = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/DeletePayment',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/payments/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            dispatch(showNotification({ message: responseModel?.message ?? 'Payment successfully deleted', type: NotificationTypes.Success }));
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ImportTurnover = createAsyncThunk<ImportTurnoverResponse, ImportTurnoverRequest, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/ImportTurnover',
    async (payment: ImportTurnoverRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/payments/import-turnover', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(payment)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ImportTurnoverResponse;
            // Every imported row gets an id of its own; the dialog works through them one by one.
            const responseModel: ImportTurnoverResponse = {
                ...createResponse,
                payments: createResponse.payments.map(payment => ({ ...payment, id: GenerateGUID(), costCenterId: null })),
            };
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

/** The AI agent's suggestions for the imported turnovers. Nothing is saved. */
export const MatchTurnovers = createAsyncThunk<TurnoverMatchResponse, TurnoverMatchRequest, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/MatchTurnovers',
    async (request: TurnoverMatchRequest, { rejectWithValue, dispatch, signal }) => {
        const response = await fetch(baseUri + 'api/payments/match-turnovers', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
            signal
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TurnoverMatchResponse | TurnoverMatchSuggestion[];
            // An API from before the rules answers with the suggestions alone.
            const matchResponse: TurnoverMatchResponse = Array.isArray(responseModel) ? { suggestions: responseModel, rules: [] } : responseModel;
            return matchResponse;
        }
        else {
            // A 502 from the agent comes as problem details; anything else may have no JSON body.
            const errorResponse = await response.json().catch(() => ({})) as Partial<ErrorResponse>;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail ?? ERROR_FALLBACK, status: response.status });
        }
    }
);

export const CreatePaymentBatch = createAsyncThunk<CreatePaymentBatchResponse, CreatePaymentBatchRequest, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/CreatePaymentBatch',
    async (payments: CreatePaymentBatchRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/payments/create-batch', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(payments)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as CreatePaymentBatchResponse;
            // When copies need confirming, the dialog asks first and nothing has been created yet.
            if (!createResponse.confirmationNeeded) {
                dispatch(showNotification({ message: createResponse.message ?? 'Payments are successfully created.', type: NotificationTypes.Success }));
            }
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//Get Existing procedures
export const GetCostCenterProcedures = createAsyncThunk<ListModel<string>[], string, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/GetCostCenterProcedures',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/financial-procedures/by-cost-center/' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ListModel<string>[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#region Drafts

export const SavePaymentsAsDraft = createAsyncThunk<MainResponse, PaymentDraftRequest, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/SavePaymentsAsDraft',
    async (draft: PaymentDraftRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/payments/draft/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(draft)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            dispatch(GetAllDrafts());
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetAllDrafts = createAsyncThunk<PaymentDraftModelForList[], void, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/GetAllDrafts',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/payments/draft/all', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PaymentDraftModelForList[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetDraftById = createAsyncThunk<DraftDataResponseWithDropdowns, string, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/GetDraftById',
    async (id: string, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/payments/draft/get?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json();
            const parsedData = JSON.parse(responseModel.draft.data)
            const draftResponse = { ...responseModel.draft, data: parsedData }
            const fullResponse = { ...responseModel, draft: draftResponse }
            return fullResponse as DraftDataResponseWithDropdowns;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateDraft = createAsyncThunk<MainResponse, UpdatePaymentDraftRequest, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/UpdateDraft',
    async (draft: UpdatePaymentDraftRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/payments/draft/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(draft)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as MainResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteDraft = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/PaymentSlice/DeleteDraft',
    async (id: string, { rejectWithValue, dispatch }) => {

        const response = await fetch(baseUri + 'api/payments/draft/delete?id=' + id, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MainResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? ERROR_FALLBACK, type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

//#endregion

// Only the lists live here. A single payment, a draft or an imported statement is loaded by the
// dialog that shows it and kept there while it is open.
const initialValue: PaymentSliceModel = {
    listOfPayments: [],
    dropdownLists: null,
    listOfDrafts: [],
}

export const PaymentSlice = createSlice({
    name: 'PaymentSlice',
    initialState: initialValue,
    reducers: {
        resetPaymentSlice: (state) => {
            state.listOfPayments = [];
            state.dropdownLists = null;
        },
    },
    extraReducers(builder) {
        builder.addCase(GetCreateData.fulfilled, (state, action: PayloadAction<PaymentDropdownLists>) => {
            state.dropdownLists = action.payload;
        });
        builder.addCase(GetAllPayments.fulfilled, (state, action: PayloadAction<PaymentModelForList[]>) => {
            state.listOfPayments = action.payload;
        });
        // The deleted payment leaves the list here; nothing is fetched again.
        builder.addCase(DeletePayment.fulfilled, (state, action) => {
            state.listOfPayments = state.listOfPayments.filter(p => p.id !== action.meta.arg);
        });
        builder.addCase(GetAllDrafts.fulfilled, (state, action: PayloadAction<PaymentDraftModelForList[]>) => {
            state.listOfDrafts = action.payload;
        });
        // The deleted draft leaves the list here; nothing is fetched again.
        builder.addCase(DeleteDraft.fulfilled, (state, action) => {
            state.listOfDrafts = state.listOfDrafts.filter(d => d.id !== action.meta.arg);
        });
    }
});

export const { resetPaymentSlice } = PaymentSlice.actions;
export default PaymentSlice.reducer;
